import type { FC } from 'react';
import PropTypes from 'prop-types';
import { MenuItem } from '@material-ui/core';
import { InputField } from './input-field';
import { AssetField } from '../types/asset-config';
import { fieldLabel } from '../config/asset-config';

interface AssetFieldInputProps {
  field: AssetField;
  value: unknown;
  error?: boolean;
  helperText?: React.ReactNode;
  onChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  disabled?: boolean;
  /** Formik field name for the input (e.g. `publicMetadata.weight`). */
  name: string;
  /** Omit the built-in label (when the surrounding layout renders its own). */
  hideLabel?: boolean;
}

/**
 * Renders a single dynamic item field from an asset-config schema:
 * - string    → text input
 * - number    → numeric input, with the field's unit as an end adornment
 * - multiline → multi-line text area
 * - boolean   → Yes/No select (stored as 'true'/'false')
 */
export const AssetFieldInput: FC<AssetFieldInputProps> = ({
  field,
  value,
  error,
  helperText,
  onChange,
  onBlur,
  disabled,
  name,
  hideLabel,
}) => {
  const isNumber = field.type === 'number';
  const isMultiline = field.type === 'multiline';

  if (field.type === 'boolean') {
    // Route through InputField (same filled variant + input padding) so the select
    // matches the height of the text fields around it.
    return (
      <InputField
        select
        label={hideLabel ? undefined : fieldLabel(field)}
        name={name}
        required={field.required}
        error={error}
        helperText={helperText}
        onChange={onChange}
        onBlur={onBlur}
        disabled={disabled}
        fullWidth
        value={value ?? ''}
      >
        <MenuItem value="">—</MenuItem>
        <MenuItem value="true">Yes</MenuItem>
        <MenuItem value="false">No</MenuItem>
      </InputField>
    );
  }

  return (
    <InputField
      InputLabelProps={{ shrink: true }}
      label={hideLabel ? undefined : fieldLabel(field)}
      name={name}
      type={isNumber ? 'number' : 'text'}
      multiline={isMultiline}
      minRows={isMultiline ? 3 : undefined}
      required={field.required}
      error={error}
      helperText={helperText}
      onChange={onChange}
      onBlur={onBlur}
      disabled={disabled}
      fullWidth
      value={value ?? ''}
    />
  );
};

AssetFieldInput.propTypes = {
  // @ts-ignore – AssetField typed above
  field: PropTypes.object.isRequired,
  value: PropTypes.any,
  error: PropTypes.bool,
  helperText: PropTypes.node,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  disabled: PropTypes.bool,
  name: PropTypes.string.isRequired,
  hideLabel: PropTypes.bool,
};
